/**
 * 带格式化功能的 CodeMirror 编辑器组件
 */

import React, { useState, useCallback, useEffect } from 'react';

import styled from 'styled-components';
import CodeMirror from '@uiw/react-codemirror';
import { Button, Toast } from '@douyinfe/semi-ui';
import { IconCode, IconExpand, IconShrink } from '@douyinfe/semi-icons';
import { oneDark } from '@codemirror/theme-one-dark';
import { sql } from '@codemirror/lang-sql';
import { javascript } from '@codemirror/lang-javascript';

import { getCodeMirrorConfig, formatByMode } from '../../utils';

// 样式组件
const EditorContainer = styled.div<{ $isFullscreen?: boolean; $autoHeight?: boolean }>`
  position: ${(props) => (props.$isFullscreen ? 'fixed' : 'relative')};
  top: ${(props) => (props.$isFullscreen ? '0' : 'auto')};
  left: ${(props) => (props.$isFullscreen ? '0' : 'auto')};
  right: ${(props) => (props.$isFullscreen ? '0' : 'auto')};
  bottom: ${(props) => (props.$isFullscreen ? '0' : 'auto')};
  width: ${(props) => (props.$isFullscreen ? '100vw' : 'auto')};
  height: ${(props) =>
    props.$isFullscreen ? '100vh' : props.$autoHeight ? 'calc(100vh - 200px)' : 'auto'};
  z-index: ${(props) => (props.$isFullscreen ? '9999' : 'auto')};
  background: ${(props) => (props.$isFullscreen ? '#fff' : 'transparent')};
  border: 1px solid #e0e0e0;
  border-radius: ${(props) => (props.$isFullscreen ? '0' : '4px')};
  overflow: hidden;
  display: flex;
  flex-direction: column;

  ${(props) =>
    props.$isFullscreen &&
    `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%),
                  radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%),
                  radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.1) 0%, transparent 50%);
      pointer-events: none;
      z-index: 1;
    }
  `}
`;

const ButtonGroup = styled.div<{ $isFullscreen?: boolean }>`
  display: flex;
  gap: 8px;
  z-index: 10;
  ${(props) =>
    props.$isFullscreen
      ? `
    position: absolute;
    top: 0;
    right: 0;
    padding: 16px;
    z-index: 3;
  `
      : `
    position: relative;
    padding: 4px 8px;
    justify-content: flex-end;
    background: #f8f9fa;
    border-bottom: 1px solid #e0e0e0;
  `}
`;

const ActionButton = styled(Button)`
  opacity: 0.8;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

const CodeMirrorWrapper = styled.div<{ $isFullscreen?: boolean }>`
  flex: 1;
  position: relative;
  z-index: 2;
  overflow: auto;
  max-height: 600px;

  .CodeMirror {
    height: ${(props) => (props.$isFullscreen ? '100%' : 'auto')};
    min-height: ${(props) => (props.$isFullscreen ? '100%' : '120px')};
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 13px;
    background: ${(props) =>
      props.$isFullscreen ? 'rgba(30, 30, 30, 0.95)' : 'transparent'} !important;
  }

  .CodeMirror-scroll {
    min-height: ${(props) => (props.$isFullscreen ? '100%' : '120px')};
    background-color: black;
  }

  /* CodeMirror 6 滚动条样式 */
  .cm-scroller {
    overflow: visible !important;
    max-height: none;
  }

  /* CodeMirrorWrapper 自身的滚动条样式 */
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
  }

  ${(props) =>
    props.$isFullscreen &&
    `
    .CodeMirror-gutters {
      background: rgba(40, 40, 40, 0.9) !important;
      border-right: 1px solid rgba(255, 255, 255, 0.1) !important;
    }

    .CodeMirror-linenumber {
      color: rgba(255, 255, 255, 0.5) !important;
    }

    .CodeMirror-cursor {
      border-left: 2px solid rgba(171, 181, 255, 0.8) !important;
    }

    /* 全屏模式下的滚动条样式 */
    .cm-scroller::-webkit-scrollbar-track {
      background: rgba(255, 255, 255, 0.05);
    }

    .cm-scroller::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
    }

    .cm-scroller::-webkit-scrollbar-thumb:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  `}
`;

export interface CodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  mode?: string | { name: string; json?: boolean };
  placeholder?: string;
  readOnly?: boolean;
  enableFormat?: boolean;
  height?: string | number;
  className?: string;
  autoHeight?: boolean; // 新增：是否自动扩展到剩余高度
}

export const CodeEditor: React.FC<CodeEditorProps> = ({
  value,
  onChange,
  mode = 'javascript',
  placeholder,
  readOnly = false,
  enableFormat = true,
  height,
  className,
  autoHeight = false,
}) => {
  const [isFormatting, setIsFormatting] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // 格式化代码
  const handleFormat = useCallback(async () => {
    if (!value?.trim() || readOnly) return;

    setIsFormatting(true);
    try {
      const formattedCode = await formatByMode(value, mode);
      onChange(formattedCode);
      Toast.success('代码格式化成功');
    } catch (error) {
      console.error('格式化失败:', error);
      Toast.error(`格式化失败: ${error instanceof Error ? error.message : '未知错误'}`);
    } finally {
      setIsFormatting(false);
    }
  }, [value, mode, onChange, readOnly]);

  // 切换全屏
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  // 监听ESC键退出全屏
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    if (isFullscreen) {
      document.addEventListener('keydown', handleKeyDown);
      // 防止页面滚动
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isFullscreen]);

  // 获取语言扩展
  const getLanguageExtension = () => {
    const modeStr = typeof mode === 'string' ? mode : mode?.name || 'javascript';
    switch (modeStr) {
      case 'sql':
        return [sql()];
      case 'javascript':
      case 'json':
      default:
        return [javascript()];
    }
  };

  const extensions = [...getLanguageExtension(), ...(isFullscreen ? [oneDark] : [])];

  return (
    <EditorContainer className={className} $isFullscreen={isFullscreen} $autoHeight={autoHeight}>
      <ButtonGroup $isFullscreen={isFullscreen}>
        {enableFormat && !readOnly && (
          <ActionButton
            icon={<IconCode />}
            size="small"
            theme="borderless"
            type="tertiary"
            loading={isFormatting}
            onClick={handleFormat}
            disabled={!value?.trim()}
            style={{
              backgroundColor: isFullscreen ? 'rgba(171,181,255,0.2)' : 'rgba(171,181,255,0.3)',
              borderRadius: '8px',
              color: '#000',
              border: isFullscreen ? '1px solid rgba(171,181,255,0.3)' : 'none',
              backdropFilter: isFullscreen ? 'blur(10px)' : 'none',
            }}
          >
            格式化
          </ActionButton>
        )}

        <ActionButton
          icon={isFullscreen ? <IconShrink /> : <IconExpand />}
          size="small"
          theme="borderless"
          type="tertiary"
          onClick={toggleFullscreen}
          style={{
            backgroundColor: isFullscreen ? 'rgba(171,181,255,0.2)' : 'rgba(171,181,255,0.3)',
            borderRadius: '8px',
            color: '#000',
            border: isFullscreen ? '1px solid rgba(171,181,255,0.3)' : 'none',
            backdropFilter: isFullscreen ? 'blur(10px)' : 'none',
          }}
          title={isFullscreen ? '退出全屏 (ESC)' : '全屏编辑'}
        >
          {isFullscreen ? '退出全屏' : '全屏'}
        </ActionButton>
      </ButtonGroup>

      <CodeMirrorWrapper $isFullscreen={isFullscreen}>
        <CodeMirror
          value={value}
          height={
            isFullscreen
              ? '100%'
              : autoHeight
              ? '100%'
              : typeof height === 'string'
              ? height
              : `${height || 200}px`
          }
          extensions={extensions}
          onChange={(val) => {
            onChange(val);
          }}
          placeholder={placeholder}
          readOnly={readOnly}
          basicSetup={{
            lineNumbers: true,
            autocompletion: true,
            bracketMatching: true,
            indentOnInput: true,
            foldGutter: true,
            searchKeymap: true,
          }}
        />
      </CodeMirrorWrapper>
    </EditorContainer>
  );
};

export default CodeEditor;
